"use client";
import { Alert, Collapse, message } from "antd";
import { isEqual } from "lodash";
import type { FC, MutableRefObject } from "react";
import { createContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@/shared/MiButton/MiButton";
import Spin from "@/shared/Spin";
import type { RootState } from "../WellLoggingOperations/types";
import { Breadcrumbs } from "./Breadcrumds";
import { ExportModal } from "./ExportModal";
import { GeoChart } from "./GeoChart/GeoChart";
import { InitGeoProfilesModal } from "./InitModal/ui/InitGeoProfilesModal";
import type { GeoDataType } from "./types";
import { UserProfilesModal } from "./UserProfilesModal/UserProfilesModal";
import {
	checkGeoData,
	exportToExcel,
	prepareGeoData,
	useActiveGroupWellIds,
} from "./utils";

type GeoTestPageProps = {
	containerRef: MutableRefObject<HTMLDivElement>;
};

export const GeoDataContext = createContext<Pick<
	GeoDataType,
	"divColumnsBodyRef" | "divColumnsHeaderRef" | "svgBodyRef"
> | null>(null);

const GeoTestPage: FC<GeoTestPageProps> = ({ containerRef }) => {
	const divColumnsHeaderRef = useRef<HTMLDivElement>(null);
	const divColumnsBodyRef = useRef<HTMLDivElement>(null);
	const svgBodyRef = useRef<SVGSVGElement>(null);

	const lists = useSelector((state: RootState) => state.lists);
	const currentIds = useActiveGroupWellIds();
	const dispatch = useDispatch();

	const { geoData, isLoading, dataError, wellIds } = useSelector(
		(store: RootState) => store.geoTest,
	);
	const { setDataError } = dispatch.geoTest;

	const [messageApi, contextHolder] = message.useMessage();

	const [modalVisible, setModalVisible] = useState<boolean>(true);

	const [exportModalVisible, setExportModalVisible] = useState<boolean>(false);
	const [userProfilesModalVisible, setUserProfilesModalVisible] =
		useState<boolean>(false);

	useEffect(() => {
		setModalVisible(true);
	}, [lists.activeItemKey]);

	const foundedErrors = checkGeoData(geoData);

	const profilesVisibility = currentIds
		? isEqual(currentIds.sort(), wellIds.sort())
		: true;

	return (
		<GeoDataContext.Provider
			value={{ svgBodyRef, divColumnsBodyRef, divColumnsHeaderRef }}
		>
			<Breadcrumbs />
			{contextHolder}
			<div className="p-2 px-4 border-b bg-white dark:bg-block-secondary dark:border-fill-primary">
				<Button
					type="default"
					className="justify-end border-2 border-black text-left text-gray-800 hover:border-gray-600 focus:ring-2 focus:ring-gray-400 dark:text-gray-100"
					onClick={() => setModalVisible(true)}
					disabled={
						!(profilesVisibility && geoData && foundedErrors.length === 0)
					}
				>
					Изменить параметры
				</Button>
				<Button
					type="default"
					className="ml-4"
					onClick={() => setExportModalVisible(true)}
					disabled={
						!(profilesVisibility && geoData && foundedErrors.length === 0)
					}
				>
					Экспорт в SVG
				</Button>

				<Button
					type="default"
					className="ml-4"
					onClick={() => setUserProfilesModalVisible(true)}
				>
					Геологические профиля
				</Button>
			</div>
			{geoData && prepareGeoData(geoData).wells.length === 1 && (
				<Alert
					className="mt-2"
					message="Информация по пластам получена только по 1 скважине. Построение отменено."
					type="error"
				/>
			)}
			{profilesVisibility &&
				!isLoading &&
				geoData &&
				prepareGeoData(geoData).wells.length > 1 &&
				foundedErrors.length === 0 && <GeoChart />}
			{modalVisible && (
				<InitGeoProfilesModal
					visible={modalVisible}
					onClose={() => setModalVisible(false)}
					onCancel={() => setModalVisible(false)}
					messageApi={messageApi}
					containerRef={containerRef}
				/>
			)}
			{exportModalVisible && geoData && (
				<ExportModal
					title="Экспорт svg"
					width={400}
					footer={null}
					open={exportModalVisible}
					onClose={() => setExportModalVisible(false)}
					onCancel={() => setExportModalVisible(false)}
					onOk={() => setExportModalVisible(false)}
					okText="Экспорт"
				/>
			)}
			{userProfilesModalVisible && (
				<UserProfilesModal
					title="Геологические профиля"
					width={800}
					open={userProfilesModalVisible}
					onCancel={() => setUserProfilesModalVisible(false)}
					messageApi={messageApi}
				/>
			)}
			{isLoading && (
				<Spin>
					<div className="h-56 w-full"></div>
				</Spin>
			)}
			{foundedErrors.length > 0 && (
				<Alert
					message={
						<Collapse
							className="flex"
							ghost
							items={[
								{
									key: 0,
									label: (
										<div>
											<span>Пропущены данные для отображения</span>
											<Button
												size="small"
												type="link"
												className="border-none"
												onClick={() => exportToExcel(foundedErrors)}
											>
												скачать отчет
											</Button>
										</div>
									),
									children: foundedErrors.map(({ errorString }) => (
										<div className="ml-6" key={errorString}>
											- {errorString}
										</div>
									)),
								},
							]}
						/>
					}
					type="error"
				/>
			)}
			{dataError && (
				<Alert
					message="Ошибка при загрузке данных"
					type="error"
					closable
					onClose={() => setDataError(false)}
				/>
			)}
		</GeoDataContext.Provider>
	);
};

export default GeoTestPage;
